import React, { useState, useEffect, useRef, useCallback } from 'react';

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  startAnimation?: boolean;
  delay?: number;
  className?: string;
  enableIntersectionObserver?: boolean;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  end,
  duration = 2000,
  suffix = '',
  prefix = '',
  decimals = 0,
  startAnimation = false,
  delay = 0,
  className = '',
  enableIntersectionObserver = true
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const countRef = useRef<HTMLSpanElement>(null);
  const animationRef = useRef<number>();
  const observerRef = useRef<IntersectionObserver>();

  // Memoized easing function for better performance
  const easeOutQuart = useCallback((t: number) => 1 - Math.pow(1 - t, 4), []);

  // Setup intersection observer
  useEffect(() => {
    if (!enableIntersectionObserver || !countRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      { 
        threshold: 0.3, // Trigger when 30% visible
        rootMargin: '50px' // Start animation 50px before element is visible
      }
    );

    observerRef.current.observe(countRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasAnimated, delay, enableIntersectionObserver]);

  // Animation logic
  useEffect(() => {
    const shouldAnimate = enableIntersectionObserver ? isVisible : startAnimation;
    
    if (shouldAnimate && !hasAnimated) {
      setHasAnimated(true);
      let startTime: number;
      let startCount = 0;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        
        // Apply easing function for smooth animation
        const easedProgress = easeOutQuart(progress);
        const currentCount = startCount + (end - startCount) * easedProgress;
        
        setCount(currentCount);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // Ensure we end exactly at the target number
          setCount(end);
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, startAnimation, end, duration, hasAnimated, easeOutQuart, enableIntersectionObserver]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const formatNumber = (num: number) => {
    if (decimals > 0) {
      return num.toFixed(decimals);
    }
    return Math.floor(num).toLocaleString();
  };

  return (
    <span 
      ref={countRef} 
      className={`tabular-nums font-bold transition-all duration-300 ${
        hasAnimated ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
      } ${className}`}
      style={{
        transform: hasAnimated ? 'scale(1)' : 'scale(0.95)',
        transition: 'transform 0.6s ease-out, opacity 0.6s ease-out'
      }}
    >
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
};

export default AnimatedCounter;